import { FC, useEffect, useState } from "react";
import {
  Flex,
  FlexProps,
  FormControl,
  FormErrorMessage,
  FormLabel,
  useRadioGroup,
} from "@chakra-ui/react";
import { useField, useFormikContext } from "formik";
import { RadioCard } from "./RadioCard";

interface Props {
  autoAdvance?: boolean | ((option: string) => boolean);
  defaultValue?: string;
  id: string;
  label?: string;
  radioGroupProps?: FlexProps;
  values: string[];
}

export const RadioInput: FC<Props> = ({
  autoAdvance,
  defaultValue,
  id,
  label,
  radioGroupProps,
  values,
}) => {
  const [field, meta] = useField(id);
  const formik = useFormikContext();
  const { getRootProps, getRadioProps } = useRadioGroup({
    name: id,
    defaultValue: defaultValue ?? "",
  });
  const [interacted, setInteracted] = useState(false);
  const [shouldAdvance, setShouldAdvance] = useState(false);

  const { submitForm } = formik;
  const { value } = field;

  useEffect(() => {
    if (!interacted) return;

    if (typeof autoAdvance === "function") {
      autoAdvance(value) && setShouldAdvance(true);
    } else if (autoAdvance) {
      setShouldAdvance(true);
    }
  }, [autoAdvance, value, interacted, submitForm]);

  useEffect(() => {
    if (shouldAdvance) {
      submitForm();
    }
  }, [shouldAdvance, submitForm]);

  const handleChange = (e: React.ChangeEvent<any>) => {
    field.onChange(e);
    setInteracted(true);
  };
  const group = getRootProps();

  return (
    <FormControl isInvalid={meta.touched && !!meta.error}>
      {label && <FormLabel htmlFor={id}>{label}</FormLabel>}

      <Flex
        direction="column"
        id={id}
        w="full"
        {...radioGroupProps}
        {...field}
        {...group}
        onChange={handleChange}
        gap={2}
      >
        {values.map((value) => {
          const radio = getRadioProps({ value });
          return <RadioCard key={value} {...radio} />;
        })}
      </Flex>

      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
};
